<script setup lang="ts">
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/shad/ui/dropdown-menu'
import useAuthStore from '@/stores/auth'
import { useUsersStore } from '@/stores/users.store'
import { ROLE_META } from '@/stores/userRoles.store'
import { ChevronDownIcon, LogOutIcon, UserIcon } from '@lucide/vue'
import { computed } from 'vue'

// composables
const authStore = useAuthStore()
const usersStore = useUsersStore()

// computed
const name = computed(() => authStore.currentUser?.name)
// No name yet because the users list is still being read (retries included),
// not because the read failed.
const loading = computed(() => !name.value && usersStore.loadingItems)
// The token's username (the email) still identifies the caller when the users
// list failed to load and there's no name to show.
const label = computed(() => {
  const username = authStore.claims?.username
  return name.value || (typeof username === 'string' ? username : 'Account')
})
const initials = computed(() =>
  name.value
    ?.split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join(''),
)
const roleLabel = computed(
  () => ROLE_META.find((role) => authStore.roles.includes(role.value))?.label,
)
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger class="flex items-center gap-x-2 outline-none">
      <template v-if="loading">
        <span
          class="mb-0.5 h-7 w-7 animate-pulse rounded-md bg-muted"
          data-testid="user-menu-loading"
        />
        <span class="hidden h-4 w-24 animate-pulse rounded bg-muted sm:block" />
      </template>
      <template v-else>
        <span
          class="mb-0.5 flex h-7 w-7 items-center justify-center rounded-md bg-slate-200 text-xs font-bold text-black"
        >
          <template v-if="initials">{{ initials }}</template>
          <UserIcon v-else :size="16" data-testid="user-menu-fallback-icon" />
        </span>
        <span class="hidden text-sm text-foreground sm:inline">{{
          label
        }}</span>
      </template>
      <ChevronDownIcon :size="16" class="mb-0.5 text-muted-foreground" />
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" class="min-w-48">
      <DropdownMenuLabel class="font-normal text-muted-foreground">{{
        roleLabel
      }}</DropdownMenuLabel>
      <DropdownMenuSeparator />
      <RouterLink :to="{ name: 'settings:account' }">
        <DropdownMenuItem>
          <UserIcon :size="16" class="mb-1 mr-2" />
          Account
        </DropdownMenuItem>
      </RouterLink>
      <RouterLink :to="{ name: 'logout' }">
        <DropdownMenuItem>
          <LogOutIcon :size="16" class="mb-1 mr-2" />
          Log out
        </DropdownMenuItem>
      </RouterLink>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
